import React, { useState, useEffect, SyntheticEvent } from 'react';

import { ICtaDestinationFields, IPageFields } from '@/types/contentful';

import { usePageDataContext, UrlByEnv } from '../../../context/PageData';
import ConsentService from '../../../services/consent';

import { trackWithFingerprint } from '../../../utils/trackWithFingerprint';
import { trackRumEvent } from '../../../utils/datadogRum';
import { getAnonymousId } from '../../../utils/getAnonymousId';
import errors from '../../../utils/errors';
import { isValidPhoneNumber, emailRegex } from '../../../utils/regex';
import { getRedirectByEnv, appendParams } from '../../../utils/buildWebRegUrl';
import { resolveCtaUrl } from '../../../utils/resolveCtaUrl';

function isProtocolUrl(url: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(url);
}

function isNonHttpProtocolUrl(url: string): boolean {
  if (!isProtocolUrl(url)) {
    return false;
  }
  const lowerUrl = url.toLowerCase();
  return !lowerUrl.startsWith('http://') && !lowerUrl.startsWith('https://');
}

function isMailtoUrl(url: string): boolean {
  return url.toLowerCase().startsWith('mailto:');
}

function normalizeMailtoUrl(url: string): string {
  if (!isMailtoUrl(url)) {
    return url;
  }

  const mailtoPattern = /^mailto:\s*([^?]+)(\?.*)?$/i;
  const match = url.match(mailtoPattern);

  if (!match) {
    return url;
  }

  const emailPart = match[1]?.trim();
  const queryPart = match[2] || '';

  if (!emailPart) {
    return url;
  }

  return `mailto:${emailPart}${queryPart}`;
}
import analytics from '../../../utils/analytics';
import { usePublicRuntimeConfig } from '../../../hooks/usePublicRuntimeConfig';

import { CtaWidgetMap } from './CtaWidgetMap';

/** Deferred import so mixpanel chunk loads only when tracking (reduces unused JS). */
async function trackExperiment(
  eventName: string,
  properties: Record<string, unknown> = {}
) {
  const { trackWithExperimentProperties } = await import('~/utils/mixpanel');
  return trackWithExperimentProperties(eventName, properties);
}

import {
  SharedProps,
  DEFAULT_LABEL,
  DEFAULT_EMAIL_LABEL,
  ctaWidgetEnum,
  RegistrationEntrypoint,
  DEFAULT_PLACEHOLDER,
  InputProps
} from '../shared';
import { IDualCtaDestinationFields } from '@/types/contentful';
import { TextInputType } from '../../FormFields/shared';
import { setCookie } from 'cookies-next';

import { getCookie } from '~/utils/cookies';
import { initialize } from '~/lib/onboarding/initialize';
import axios from 'axios';
import cn from 'classnames';

interface WebRegWidgetProps extends SharedProps, RegistrationEntrypoint {
  module?: string;
  height?: string | number;
  width?: string | number;
  bypassPhoneNumberEntry?: boolean;
  isReturning?: boolean;
  additionalSegmentData?: Record<string, unknown>;
  'aria-label'?: string;
}

export function Widgets({
  id,
  className,
  module,
  forwardedCtaParams,
  designType,
  label,
  placeholder = DEFAULT_PLACEHOLDER,
  errorPosition = 'bottom',
  showErrorBanner = true,
  sendConsentEvent = false,
  height = '100%',
  width = 'fit-content',
  bypassPhoneNumberEntry = false,
  url,
  openInNewTab,
  autocompleteAttribute = 'tel',
  segmentEventName,
  additionalSegmentData,
  successMessage,
  intent,
  'aria-label': ariaLabel,
  ...widgetProps
}: WebRegWidgetProps) {
  const isEmailWidget =
    [ctaWidgetEnum.EMAIL].includes(designType as ctaWidgetEnum) ||
    autocompleteAttribute?.includes('email');

  const isRoleWidget = designType === ctaWidgetEnum.ROLE_SELECT_WEBREG;

  // Use email label when in email mode
  const effectiveLabel =
    label || (isEmailWidget ? DEFAULT_EMAIL_LABEL : DEFAULT_LABEL);

  const [value, setValue] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>();

  const { onboardingUrl } = usePublicRuntimeConfig();
  const { page, webregIncident = false } = usePageDataContext();
  const { advocateUserData } = usePageDataContext();
  const {
    webRegUrl,
    stagingWebRegUrl,
    previewWebRegUrl,
    forwardedUrlParams,
    intent: pageIntent,
    promoCode
  } = (page as IPageFields) ?? {};

  const unformattedPhoneNumber = value.replace(/\D/g, '');

  const sendSMS = async () => {
    try {
      await axios.post('/api/send-sms', {
        phoneNumber: unformattedPhoneNumber
      });
    } catch (error) {
      console.error('Error sending SMS:', error);
      setSubmitted(false);
      setErrorMessage('Failed to send SMS');
    }
  };

  useEffect(() => {
    const setFromEvent = (e: MouseEvent) => {
      // Don't clear error message if clicking on the CTA widget itself or submit buttons
      const target = e.target as Element;
      const ctaContainer = target.closest('[data-testid="cta"]');
      const isSubmitButton =
        target.closest('button[type="submit"]') ||
        target.closest('[data-e2e="btn-getting-started"]') ||
        target.closest('[data-testid="btn-getting-started"]');

      if (!ctaContainer && !isSubmitButton) {
        setErrorMessage('');
      }
    };
    window.addEventListener('mousedown', setFromEvent);

    return () => {
      window.removeEventListener('mousedown', setFromEvent);
    };
  }, []);

  const validate = () => {
    let errMsg;

    switch (designType) {
      case ctaWidgetEnum.EMAIL:
        if (!value.match(emailRegex)) {
          return errors.INVALID_EMAIL;
        }

        return errMsg;

      case ctaWidgetEnum.SMS_REG:
      case ctaWidgetEnum.MOBILE_PHONE_NUMBER:
      case ctaWidgetEnum.MOBILE_PHONE_NUMBER_DROPSHADOW:
      case ctaWidgetEnum.GET_STARTED:
      case ctaWidgetEnum.LOADER_STICKY_BUTTON:
        if (isEmailWidget && !value.match(emailRegex)) {
          return errors.INVALID_EMAIL;
        }

        if (
          bypassPhoneNumberEntry === false &&
          !isEmailWidget &&
          !isValidPhoneNumber(unformattedPhoneNumber)
        ) {
          if (designType === ctaWidgetEnum.LOADER_STICKY_BUTTON) {
            return errors.UPDATED_INVALID_PHONE;
          }
          return errors.INVALID_PHONE;
        }
        return errMsg;

      case ctaWidgetEnum.GET_STARTED_NO_PHONE:
      case ctaWidgetEnum.STANDARD:
      case ctaWidgetEnum.GL4C_AUTH:
      case ctaWidgetEnum.GL4C_INLINE_AUTH:
      case ctaWidgetEnum.ROLE_SELECT_WEBREG:
      default:
        return errMsg;
    }
  };

  const fireAnalytics = async () => {
    const segmentValueKey = isEmailWidget ? 'email' : 'phone';
    const event = {
      page_url: window.location.href,
      widgetType: designType,
      module,
      ...additionalSegmentData,
      [segmentValueKey]: value
    };

    if (bypassPhoneNumberEntry) {
      await trackExperiment(segmentEventName || 'CTA Clicked', event);
    } else {
      let eventName = 'Phone Number Entered';

      if (isEmailWidget) {
        eventName = 'Email Address Entered';
      } else if (isRoleWidget || designType === ctaWidgetEnum.DUAL_CTA_BUTTON) {
        eventName = 'CTA Clicked';
      }

      await trackExperiment(segmentEventName || eventName, event);
    }

    if (sendConsentEvent) {
      const userId = analytics.anonymousId || false;

      await ConsentService.collectConsent({
        userId,
        type: 'affiliate_SEC_disclaimer',
        pageName: 'Affiliate Page'
      });
    }
  };

  const webregRedirect = async () => {
    const anonymousId = getAnonymousId();

    const ctaUrlField = resolveCtaUrl(
      url?.fields as Partial<ICtaDestinationFields>
    );
    const pageUrlField = getRedirectByEnv({
      production: webRegUrl,
      staging: stagingWebRegUrl,
      development: previewWebRegUrl
    });

    // Check ctf CTA field or ctf page field or use env vars
    const baseUrl = ctaUrlField || pageUrlField;
    const initParams = {
      phoneNumber: !isEmailWidget ? unformattedPhoneNumber : undefined,
      emailAddress: isEmailWidget ? value : undefined,
      bypassPhoneNumberEntry,
      forwardedUrlParams,
      forwardedCtaParams,
      advocateUserData,
      role: isRoleWidget ? value : undefined,
      anonymousId,
      intent: !!(intent || pageIntent) ? intent || pageIntent : undefined,
      promoCode
    };

    await initialize(initParams);

    if (isRoleWidget) {
      if (value === 'parent') {
        trackWithFingerprint('Primary Hero CTA - Parent');
      } else if (value === 'child') {
        trackWithFingerprint('Secondary Hero CTA - Child');
      }
    }

    // Common event tracking
    trackRumEvent('user_to_registration', {
      id: anonymousId,
      anonymousId,
      ...(isEmailWidget
        ? { emailAddress: value }
        : { phoneNumber: unformattedPhoneNumber })
    });

    if (baseUrl) {
      if (baseUrl.startsWith('#') || isNonHttpProtocolUrl(baseUrl)) {
        if (isNonHttpProtocolUrl(baseUrl)) {
          const normalizedUrl = isMailtoUrl(baseUrl)
            ? normalizeMailtoUrl(baseUrl)
            : baseUrl;
          window.location.href = normalizedUrl;
        } else {
          window.location.assign(baseUrl);
        }
      } else {
        const targetUrl = appendParams({
          baseUrl,
          ...initParams
        });
        window.location.assign(targetUrl);
      }
    } else {
      window.location.assign(onboardingUrl);
    }
  };

  const onSuccess = () => {
    switch (designType) {
      case ctaWidgetEnum.SMS_REG:
        sendSMS();
        return;
      case ctaWidgetEnum.GL4C_AUTH:
      case ctaWidgetEnum.GL4C_INLINE_AUTH:
        return;

      case ctaWidgetEnum.EMAIL:
        if (module === 'Lead Capture') {
          trackWithFingerprint('Lead Capture Form Submitted', {
            email: value
          });

          return;
        }

        trackWithFingerprint(`${segmentEventName} - Form Submitted`, {
          email: value
        });
        return;

      case ctaWidgetEnum.STANDARD: {
        const envBasedUrl = resolveCtaUrl(
          url?.fields as Partial<ICtaDestinationFields>
        );
        // Use mobileUrl on smaller viewports when provided
        const preferMobileUrl = !window.matchMedia('(min-width: 1080px)')
          .matches;
        // Support mobileUrl as either a raw string or Contentful destination
        const mobileUrlField = (
          widgetProps as Partial<WebRegWidgetProps> & {
            mobileUrl?: { fields?: UrlByEnv };
          }
        ).mobileUrl;
        const resolvedMobileUrl = resolveCtaUrl(
          (mobileUrlField?.fields || {}) as Partial<ICtaDestinationFields>
        );
        const forwardedButtonParams = (
          widgetProps as Partial<WebRegWidgetProps> & {
            forwardedButtonParams?: string;
          }
        ).forwardedButtonParams;
        const candidateUrl =
          preferMobileUrl && resolvedMobileUrl
            ? resolvedMobileUrl
            : envBasedUrl;

        if (candidateUrl) {
          if (
            candidateUrl.startsWith('#') ||
            isNonHttpProtocolUrl(candidateUrl)
          ) {
            if (openInNewTab) {
              return window.open(candidateUrl, '_blank', 'noopener,noreferrer');
            }
            if (isNonHttpProtocolUrl(candidateUrl)) {
              const normalizedUrl = isMailtoUrl(candidateUrl)
                ? normalizeMailtoUrl(candidateUrl)
                : candidateUrl;
              window.location.href = normalizedUrl;
            } else {
              window.location.assign(candidateUrl);
            }
            return;
          } else {
            const targetUrl = appendParams({
              baseUrl: candidateUrl,
              forwardedButtonParams,
              bypassPhoneNumberEntry: true,
              intent: !!(intent || pageIntent)
                ? intent || pageIntent
                : undefined
            });
            return openInNewTab
              ? window.open(targetUrl, '_blank', 'noopener,noreferrer')
              : window.location.assign(targetUrl);
          }
        }

        return webregRedirect();
      }

      case ctaWidgetEnum.DUAL_CTA_BUTTON:
        if (value === 'primary') {
          const primaryUrl = (url?.fields as IDualCtaDestinationFields)
            ?.primaryCtaUrl;
          if (primaryUrl) {
            if (isNonHttpProtocolUrl(primaryUrl)) {
              const normalizedUrl = isMailtoUrl(primaryUrl)
                ? normalizeMailtoUrl(primaryUrl)
                : primaryUrl;
              window.location.href = normalizedUrl;
            } else {
              window.location.assign(primaryUrl);
            }
          }
        } else if (value === 'secondary') {
          const secondaryUrl = (url?.fields as IDualCtaDestinationFields)
            ?.secondaryCtaUrl;
          if (secondaryUrl) {
            if (isNonHttpProtocolUrl(secondaryUrl)) {
              const normalizedUrl = isMailtoUrl(secondaryUrl)
                ? normalizeMailtoUrl(secondaryUrl)
                : secondaryUrl;
              window.location.href = normalizedUrl;
            } else {
              window.location.assign(secondaryUrl);
            }
          }
        }
        return;

      case ctaWidgetEnum.ROLE_SELECT_WEBREG:
      case ctaWidgetEnum.GET_STARTED_NO_PHONE:
      case ctaWidgetEnum.MOBILE_PHONE_NUMBER:
      case ctaWidgetEnum.MOBILE_PHONE_NUMBER_DROPSHADOW:
      case ctaWidgetEnum.GET_STARTED:
      case ctaWidgetEnum.LOADER_STICKY_BUTTON:
      default:
        if (
          segmentEventName &&
          !getCookie('userCTAInteraction')?.includes(segmentEventName)
        ) {
          const userSegments = getCookie('userCTAInteraction')
            ? getCookie('userCTAInteraction').split(',')
            : [];
          const newCookieValue =
            userSegments.length > 0
              ? userSegments.concat(segmentEventName).join(',')
              : segmentEventName;
          setCookie('userCTAInteraction', newCookieValue, {
            encode: (cookieVal: string) => cookieVal
          });
        }
        return !segmentEventName && webregRedirect();
    }
  };

  const handleOnSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    const err = validate();

    if (err) {
      setErrorMessage(err);
      trackWithFingerprint('Failed to capture CTA', {
        page_url: window.location.href,
        widgetType: designType,
        module
      });
      return;
    }

    try {
      await fireAnalytics();
    } catch (error) {
      // Analytics tracking failed, continue with navigation
    }

    setErrorMessage(null);
    setSubmitted(true);
    onSuccess();
  };

  const inputProps: InputProps = {
    id,
    type: isEmailWidget ? TextInputType.EMAIL : TextInputType.PHONE,
    value,
    placeholder,
    handleOnChange: (nextValue: string) => setValue(nextValue),
    inputMode: isEmailWidget ? 'text' : 'numeric',
    disabled: submitted && !errorMessage,
    'aria-invalid': !!errorMessage,
    ...(ariaLabel && { 'aria-label': ariaLabel })
  };

  return webregIncident && module !== 'Lead Capture' ? null : (
    <div
      className="z-3 relative flex h-full w-full flex-col items-center justify-center"
      style={{ height, width }}
    >
      <div
        className={cn(
          className,
          'z-3 relative flex h-full w-full flex-col justify-center',
          designType === ctaWidgetEnum.STANDARD ? 'items-start' : 'items-center'
        )}
        data-e2e="cta-container"
        data-testid="cta"
      >
        <form
          className="flex w-full flex-auto flex-col justify-center"
          style={{ width: width }}
          onSubmit={handleOnSubmit}
          noValidate
        >
          <CtaWidgetMap
            {...widgetProps}
            autocompleteAttribute={autocompleteAttribute}
            designType={designType as ctaWidgetEnum}
            label={effectiveLabel}
            errorMessage={errorMessage}
            errorPosition={errorPosition}
            showErrorBanner={showErrorBanner}
            submitted={submitted}
            inputProps={inputProps}
            segmentEventName={segmentEventName}
            successMessage={successMessage}
          />
        </form>
      </div>
    </div>
  );
}
